import { Component, EventEmitter, Input, OnInit, Output, inject, ChangeDetectionStrategy } from '@angular/core';
import {
  BankAccount,
  BankAccountCollection,
  BankAccountService,
  BankAccountStatusEnum, BankAccountsImportForm,
  BankConnection,
  BankConnectionMessagesEnum,
  BankConnectionService,
  BasiqService,
  BasiqUserService,
} from 'taxtank-core';
import { BaseFormComponent } from '@shared/component/base/base-form.component';
import { takeUntil } from 'rxjs';
import { combineLatest } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { BankAccountImportFormDialogComponent } from '../bank-account-import-form-dialog/bank-account-import-form-dialog.component';

/**
 * Component with form with basiq bank accounts allowed to import
 */
@Component({
  selector: 'app-bank-accounts-import-form',
  templateUrl: './bank-accounts-import-form.component.html',
  styleUrls: ['./bank-accounts-import-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BankAccountsImportFormComponent extends BaseFormComponent implements OnInit {
  @Input() connection: BankConnection;
  @Output() submitted: EventEmitter<BankAccount[]> = new EventEmitter();
  @Output() back: EventEmitter<void> = new EventEmitter();
  bankAccounts: BankAccountCollection;
  savedAccounts: BankAccountCollection;
  disconnectedBankAccounts: BankAccountCollection;
  form: BankAccountsImportForm;
  messages: typeof BankConnectionMessagesEnum = BankConnectionMessagesEnum;
  nothingSelectedErrorMessage = 'Select at least one bank account';
  isLoading = true;

  private bankAccountService = inject(BankAccountService);
  private basiqService = inject(BasiqService);
  private basiqUserService = inject(BasiqUserService);
  private connectionService = inject(BankConnectionService);
  private dialog = inject(MatDialog);

  ngOnInit(): void {
    combineLatest([this.bankAccountService.get(), this.basiqService.get()])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([bankAccounts, basiqBankAccounts]) => {
        const savedAccounts = bankAccounts.filterBy('bankConnection.id', this.connection.id);
        this.savedAccounts = savedAccounts.getConnected();
        this.bankAccounts = basiqBankAccounts.filterBy('bankConnection.id', this.connection.id)
          .removeBy('accountId', this.savedAccounts.mapBy('accountId')).getConnected();
        this.disconnectedBankAccounts = savedAccounts.filterBy('status', BankAccountStatusEnum.DISCONNECTED);
        this.buildForm();
        this.isLoading = false;
      });
  }

  /**
   * for mobile only: Open separated dialog with single bank account import form
   */
  openBankAccountImportFormDialog(bankAccount: BankAccount): void {
    const dialogRef = this.dialog.open(BankAccountImportFormDialogComponent, {
      data: bankAccount
    });

    dialogRef.afterClosed().subscribe((bankAccounts: BankAccount[]) => {
      // dialog closed without submit
      if (!bankAccounts) {
        return;
      }

      // form submitted
      this.submitted.emit(bankAccounts);
    });
  }

  buildForm(): void {
    this.form = new BankAccountsImportForm(this.bankAccounts.toArray());
  }

  save(bankAccounts: BankAccount[]): void {
    this.submitted.emit(bankAccounts);
  }

  /**
   * @TODO TT-4883 vik copypast
   * @param connection
   */
  reconnect(connection: BankConnection): void {
    combineLatest([
      this.connectionService.deleteBasiq(connection),
      this.basiqUserService.redirectToBasiqConsent(connection.bank.externalId, connection.basiqUser.id)]
    ).pipe(takeUntil(this.destroy$)).subscribe();
  }
}
